import React, { useState, createContext, useEffect } from 'react';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import { setPageTitle } from '../../utils/pageTitle';
import { LayoutGrid, PackageSearch, Import, ChevronLeft, LogOut, Home, Settings, MessageSquare, Menu, Store } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import ImportDialog from '../dialogs/ImportDialog';
import { useIsAdmin } from '../../hooks/useIsAdmin';

// Create a context for the refresh function
export const RefreshContext = createContext<(() => Promise<void>) | null>(null);

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userEmail, logout } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 768);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);

  const isActive = (path: string) => {
    // Split the pathname and get the first part after /lms/
    const pathParts = location.pathname.split('/');
    const currentPath = pathParts.length >= 3 ? pathParts[2] : '';
    return currentPath === path;
  };

  const userInitials = userEmail
    ?.split('@')[0]
    ?.split('.')
    ?.map((n: string) => n[0])
    ?.join('')
    ?.toUpperCase() || '??';

  const { isAdmin } = useIsAdmin();

  // Listen for import dialog open requests from other components
  useEffect(() => {
    const handleOpenImport = () => {
      setIsImportDialogOpen(true);
    };

    window.addEventListener('openImport', handleOpenImport);
    return () => {
      window.removeEventListener('openImport', handleOpenImport);
    };
  }, []);

  // Set page title based on current route
  useEffect(() => {
    const path = location.pathname;
    let title = 'Listing Management System';
    
    if (path === '/lms' || path === '/lms/dashboard') {
      title = 'Listing Management System';
    } else if (path.startsWith('/lms/products')) {
      title = 'Products | Listing Management System';
    } else if (path.startsWith('/lms/prompts')) {
      title = 'Prompts | Listing Management System';
    } else if (path.startsWith('/lms/stores')) {
      title = 'Stores | Listing Management System';
    } else if (path.startsWith('/lms/admin')) {
      title = 'Admin | Listing Management System';
    }
    
    setPageTitle(title);
  }, [location.pathname]);

  const handleOpenImportDialog = () => {
    setIsImportDialogOpen(true);
  };

  const handleRefresh = async () => {
    // Trigger refresh in components that are listening
    const event = new CustomEvent('refreshProducts');
    window.dispatchEvent(event);
  };

  const handleLogout = async () => {
    try {
      await logout();
      // Use replace to prevent history stacking
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const handleAppsHubClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/apps', { replace: true });
  };

  const navigateTo = (path: string) => {
    navigate(`/lms/${path}`);
    setIsMobileMenuOpen(false);
  };

  return (
    <RefreshContext.Provider value={handleRefresh}>
      <div className="flex h-screen bg-gray-50 relative">
        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md"
        >
          <Menu className="h-6 w-6 text-gray-600" />
        </button>

        {/* Mobile Menu Backdrop */}
        {isMobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div 
          className={`
            ${isCollapsed && !isMobileMenuOpen ? 'w-16' : 'w-64'}
            ${!isMobileMenuOpen && 'hidden md:flex'}
            bg-white border-r transition-all duration-300 ease-in-out
            fixed md:relative
            h-full z-40
            flex flex-col
          `}
        >
          {/* Collapse Toggle - Hidden on Mobile */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="absolute -right-3 top-6 bg-white border rounded-full p-1 shadow-sm hover:bg-gray-50 hidden md:block"
          >
            <ChevronLeft className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
          </button>

          <div className="pl-1 border-b flex items-center justify-center h-42">
            <img 
              src="/logo.png" 
              alt="Logo" 
              className={`h-40 w-auto -mb-10 -mt-10 transition-opacity duration-300 ${isCollapsed && !isMobileMenuOpen ? 'opacity-0' : ''}`}
            />
          </div>
          
          <nav className="flex-1 mt-4">
            <div
              onClick={() => navigateTo('dashboard')}
              className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                isActive('dashboard') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <LayoutGrid className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
              {(!isCollapsed || isMobileMenuOpen) && 'Dashboard'}
            </div>

            <div
              onClick={() => navigateTo('products')}
              className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                isActive('products') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <PackageSearch className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
              {(!isCollapsed || isMobileMenuOpen) && 'Products'}
            </div>

            <div
              onClick={() => navigateTo('prompts')}
              className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                isActive('prompts') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <MessageSquare className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
              {(!isCollapsed || isMobileMenuOpen) && 'Prompts'}
            </div>

            <div
              onClick={() => navigateTo('stores')}
              className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                isActive('stores') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Store className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
              {(!isCollapsed || isMobileMenuOpen) && 'Stores'}
            </div>

            <div
              onClick={() => {
                handleOpenImportDialog();
                setIsMobileMenuOpen(false);
              }}
              className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                isActive('import') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Import className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
              {(!isCollapsed || isMobileMenuOpen) && 'Import'}
            </div>

            {/* Admin Link - Only shown to admin users */}
            {isAdmin && (
              <div
                onClick={() => navigateTo('admin')}
                className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                  isActive('admin') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Settings className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
                {(!isCollapsed || isMobileMenuOpen) && 'Admin'}
              </div>
            )}

            {/* Apps Hub Link */}
            <div
              onClick={handleAppsHubClick}
              className="flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer"
            >
              <Home className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
              {(!isCollapsed || isMobileMenuOpen) && 'Apps Hub'}
            </div>
          </nav>

          {/* User Info Section */}
          <div className={`border-t p-4 ${isCollapsed && !isMobileMenuOpen ? 'items-center' : ''}`}>
            {(!isCollapsed || isMobileMenuOpen) && (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">{userEmail}</span>
                </div>
                <div className="flex items-center justify-between">
                  <button 
                    onClick={handleLogout}
                    className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-2"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                  <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-blue-600">{userInitials}</span>
                  </div>
                </div>
              </>
            )}
            {isCollapsed && !isMobileMenuOpen && (
              <div className="flex flex-col items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-sm font-medium text-blue-600">{userInitials}</span>
                </div>
                <button 
                  onClick={handleLogout}
                  className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-auto bg-gray-50 pt-16 md:pt-0">
          <Outlet />
        </div>

        {/* Import Dialog */}
        <ImportDialog 
          isOpen={isImportDialogOpen}
          onOpenChange={(open) => setIsImportDialogOpen(open)}
          onImportComplete={async () => {
            await handleRefresh();
            setIsImportDialogOpen(false);
          }}
        />
      </div>
    </RefreshContext.Provider>
  );
};

export default Layout;
